import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Check, X, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const RIBManagement = () => {
  const [ribs, setRibs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRib, setEditingRib] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    bank_name: '',
    iban: '',
    bic: '',
    account_holder: '',
    is_default: false
  });

  useEffect(() => {
    loadRibs();
  }, []);

  const loadRibs = async () => {
    try {
      const res = await axios.get(`${API}/ribs`, { withCredentials: true });
      setRibs(res.data || []);
    } catch (error) {
      console.error('Error loading RIBs:', error);
      toast.error('Erreur lors du chargement des RIB');
      setRibs([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (rib = null) => {
    if (rib) {
      setEditingRib(rib);
      setFormData({
        label: rib.label,
        bank_name: rib.bank_name,
        iban: rib.iban,
        bic: rib.bic,
        account_holder: rib.account_holder,
        is_default: rib.is_default
      });
    } else {
      setEditingRib(null);
      setFormData({
        label: '',
        bank_name: '',
        iban: '',
        bic: '',
        account_holder: '',
        is_default: false
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.label || !formData.iban || !formData.bic) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      if (editingRib) {
        await axios.put(`${API}/ribs/${editingRib.id}`, formData, { withCredentials: true });
        toast.success('RIB modifié avec succès');
      } else {
        await axios.post(`${API}/ribs`, formData, { withCredentials: true });
        toast.success('RIB ajouté avec succès');
      }
      setDialogOpen(false);
      loadRibs();
    } catch (error) {
      console.error('Error saving RIB:', error);
      toast.error('Erreur lors de l\'enregistrement du RIB');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce RIB ?')) return;
    try {
      await axios.delete(`${API}/ribs/${id}`, { withCredentials: true });
      toast.success('RIB supprimé');
      loadRibs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.patch(`${API}/ribs/${id}/set-default`, {}, { withCredentials: true });
      toast.success('RIB défini par défaut');
      loadRibs();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Gestion des RIB
            </CardTitle>
            <CardDescription>
              Ajoutez et gérez vos relevés d'identité bancaire pour les inclure dans vos devis et notes d'auteur
            </CardDescription>
          </div>
          <Button onClick={() => handleOpenDialog()} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un RIB
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!Array.isArray(ribs) || ribs.length === 0 ? (
          <div className="text-center py-8 text-stone-500">
            Aucun RIB enregistré. Ajoutez-en un pour qu'il apparaisse sur vos documents.
          </div>
        ) : (
          <div className="space-y-3">
            {ribs.map((rib) => (
              <div key={rib.id} className="border rounded-lg p-4 flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold">{rib.label}</h3>
                    {rib.is_default && (
                      <Badge className="bg-green-100 text-green-700">
                        <Check className="h-3 w-3 mr-1" />
                        Par défaut
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-stone-600 space-y-1">
                    <p><span className="font-medium">Banque:</span> {rib.bank_name}</p>
                    <p><span className="font-medium">Titulaire:</span> {rib.account_holder}</p>
                    <p><span className="font-medium">IBAN:</span> {rib.iban}</p>
                    <p><span className="font-medium">BIC:</span> {rib.bic}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {!rib.is_default && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSetDefault(rib.id)}
                      title="Définir par défaut"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(rib)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(rib.id)}
                    className="text-rose-600 hover:text-rose-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRib ? 'Modifier le RIB' : 'Ajouter un RIB'}</DialogTitle>
            <DialogDescription>
              Renseignez les informations bancaires
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="label">Libellé *</Label>
              <Input
                id="label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Ex: RIB Kolnoa, RIB Personnel..."
              />
            </div>
            <div>
              <Label htmlFor="bank_name">Nom de la banque *</Label>
              <Input
                id="bank_name"
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="Ex: BNP Paribas"
              />
            </div>
            <div>
              <Label htmlFor="account_holder">Titulaire du compte *</Label>
              <Input
                id="account_holder"
                value={formData.account_holder}
                onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                placeholder="Ex: KOLNOA PRODUCTION"
              />
            </div>
            <div>
              <Label htmlFor="iban">IBAN *</Label>
              <Input
                id="iban"
                value={formData.iban}
                onChange={(e) => setFormData({ ...formData, iban: e.target.value.toUpperCase() })}
                placeholder="Ex: FR76 1234 5678 9012 3456 7890 123"
              />
            </div>
            <div>
              <Label htmlFor="bic">BIC / SWIFT *</Label>
              <Input
                id="bic"
                value={formData.bic}
                onChange={(e) => setFormData({ ...formData, bic: e.target.value.toUpperCase() })}
                placeholder="Ex: BNPAFRPPXXX"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_default" className="cursor-pointer">
                Définir comme RIB par défaut
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700">
              {editingRib ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RIBManagement;
